import React, { FC, useEffect } from 'react';
import formatBytes from '../../../helpers/format-bytes';
import ButtonSecondary from '../button-secondary/button-secondary';
import IcomoonIconButton from '../icomoon-icon-button/icomoon-icon-button';
import useUploader from './use-uploader';

interface FieldUploadProps {
  readonly disabled?: boolean;
  readonly error?: boolean;
  readonly extensions?: string[];
  readonly help?: string;
  readonly onChange?: (value: unknown[]) => void;
  readonly upload: (file: File) => unknown;
}

const FieldUpload: FC<FieldUploadProps> = ({
  disabled,
  error,
  extensions,
  help,
  onChange,
  upload,
}) => {
  const { inputRef, onInputChange, onRemove, files } = useUploader(upload);

  useEffect(
    () =>
      onChange(
        files
          .filter((file) => file.status === 'complete')
          .map((file) => file.value)
      ),
    [files]
  );

  return (
    <div>
      <div className="flex space-x-4 items-center justify-around mb-4">
        <div>
          <ButtonSecondary
            name="Upload"
            disabled={disabled}
            onClick={() => inputRef.current?.click()}
          />
        </div>

        {help && (
          <div
            className={`
            whitespace-pre-line
            ${error ? 'text-invalid' : 'text-grey-600'}
          `}
          >
            {help}
          </div>
        )}
      </div>

      <ul className="grid gap-2">
        {files.map((file) => (
          <li
            key={file.id}
            className="bg-grey-50 flex space-x-2 items-center py-1 pl-6 pr-4 rounded-lg truncate"
          >
            <span className="truncate">{file.title}</span>

            {file.status === 'complete' && (
              <span className="text-grey-600">{formatBytes(file.size)}</span>
            )}

            {file.status === 'uploading' && (
              <span className="text-grey-600">Uploading</span>
            )}

            {file.status === 'failed' && (
              <span className="text-invalid">Failed</span>
            )}

            <IcomoonIconButton
              name="close"
              size="sm"
              color="text-grey-600"
              disabled={disabled}
              onClick={() => onRemove(file)}
            />
          </li>
        ))}
      </ul>

      <input
        className="hidden"
        ref={inputRef}
        type="file"
        onChange={(event) => onInputChange(event)}
        accept={extensions?.length ? extensions.join(',') : null}
      />
    </div>
  );
};

FieldUpload.defaultProps = {
  disabled: false,
  error: false,
  extensions: null,
  help: null,
  onChange: () => ({}),
};

export default FieldUpload;
